import {LABELS} from '@github-ui/issues-shared/labels'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {SoftNavOnlyLink as Link} from '@github-ui/react-router'
import {
  getIconColorClassName,
  getIconComponent,
  SAVED_VIEW_OPEN_SOURCES,
  type SavedViewOpenLocationState,
} from '@github-ui/saved-views'
import {CopyIcon, KebabHorizontalIcon, PinIcon, TrashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, RelativeTime} from '@primer/react'
import {memo, useRef} from 'react'

import {useDuplicateSavedViewDialog} from '../../hooks/use-duplicate-saved-view-dialog'
import {type PinControls, PinViewMenuItem} from './PinViewMenuItem'
import styles from './RepositoryViews.module.css'
import type {SavedView} from './types'

type ViewRowProps = {
  view: SavedView
  owner: string
  repo: string
  canManage: boolean
  onRequestDelete: (view: SavedView, returnFocusTarget: HTMLElement | null) => void
  showPinnedBadge?: boolean
  pinControls?: PinControls
}

function ViewRowComponent({
  view,
  owner,
  repo,
  canManage,
  onRequestDelete,
  showPinnedBadge = false,
  pinControls,
}: ViewRowProps) {
  const viewUrl = `/${owner}/${repo}/issues/views/${view.id}`
  const openFromIndexState: SavedViewOpenLocationState = {savedViewOpenSource: SAVED_VIEW_OPEN_SOURCES.index}

  const viewOptionsButtonRef = useRef<HTMLButtonElement>(null)
  const {openDuplicateDialog, duplicateDialog} = useDuplicateSavedViewDialog({
    owner,
    repo,
    returnFocusRef: viewOptionsButtonRef,
  })

  return (
    <ListItem
      title={
        <ListItemTitle
          value={view.name}
          href={viewUrl}
          linkProps={{as: Link, to: viewUrl, state: openFromIndexState, 'data-saved-view-id': String(view.id)}}
        />
      }
      role="listitem"
      className={styles.viewRow}
      secondaryActions={
        canManage ? (
          <div className={styles.viewActions}>
            <ActionMenu anchorRef={viewOptionsButtonRef}>
              <ActionMenu.Anchor>
                <IconButton
                  icon={KebabHorizontalIcon}
                  aria-label={LABELS.views.viewOptionsAriaLabel(view.name)}
                  variant="invisible"
                />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay width="small">
                <ActionList>
                  <PinViewMenuItem view={view} pinControls={pinControls} />
                  <ActionList.Item onSelect={() => openDuplicateDialog(view)}>
                    <ActionList.LeadingVisual>
                      <CopyIcon />
                    </ActionList.LeadingVisual>
                    {LABELS.views.duplicate}
                  </ActionList.Item>
                  <ActionList.Divider />
                  <ActionList.Item
                    onSelect={() => onRequestDelete(view, viewOptionsButtonRef.current)}
                    variant="danger"
                  >
                    <ActionList.LeadingVisual>
                      <TrashIcon />
                    </ActionList.LeadingVisual>
                    {LABELS.views.delete}
                  </ActionList.Item>
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
            {duplicateDialog}
          </div>
        ) : undefined
      }
    >
      <ListItemLeadingVisual
        className={`${styles.viewLeadingVisual} ${getIconColorClassName(view.color)}`}
        icon={getIconComponent(view.icon)}
      />
      <ListItemMainContent>
        <ListItemDescription>
          <ListItemDescriptionItem>
            {view.creatorLogin ? <span>{LABELS.views.createdBy(view.creatorLogin)} &bull; </span> : null}
            <span>
              {LABELS.views.lastUpdated} <RelativeTime datetime={view.updatedAt} />
            </span>
            {showPinnedBadge && (
              <>
                <span> &bull; </span>
                <span className={styles.pinnedBadge}>
                  <PinIcon size={12} className={styles.pinnedIcon} />
                  <span>{LABELS.views.pinned}</span>
                </span>
              </>
            )}
          </ListItemDescriptionItem>
        </ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
}

export const ViewRow = memo(ViewRowComponent)
